import React, { FC, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetResourceQuery } from '../healthWalletSlice';
import { formatDate } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { bucketGroup, statusChip } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Conditions tab sections (design: Polygon Health Trends Redesign/Conditions
// Desktop.dc.html, in the DESIGN palette — see theme.ts). Data = get_patient_resource
// ('conditions') tier-1 groups — each section self-fetches (RTK dedupes).
// Recorded limitations: symptom/admin buckets & filters DISABLED (classifier
// deferred); search is name-only; statuses truthful post re-sync.
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
const CARD_LIMIT = 6;

// SINGLE source of truth for the tab's buckets — hero tiles, filter pills, and sections all iterate THIS list
// (present-only). `group` = the bucketGroup() return this bucket collects ('symptom' vs the plural 'symptoms'
// key); `label` = pill + hero tile; `section` = section header; `dot` = section dot; `tileBg/tileFg` = tile.
// (The disabled "Records"/Administrative placeholders stay separate — deferred classifier, not real buckets.)
const COND_BUCKETS: Array<{ key: 'current' | 'past' | 'symptoms'; group: 'current' | 'past' | 'symptom'; label: string; section: string; dot: string; tileBg: string; tileFg: string }> = [
  { key: 'current',  group: 'current', label: 'Current',  section: 'Current health conditions', dot: '#3b7dd8', tileBg: '#eef3fb',      tileFg: '#2f5fb0' },
  { key: 'past',     group: 'past',    label: 'Past',     section: 'Past conditions',           dot: '#b0b0b6', tileBg: '#f0f0f3',      tileFg: '#48484a' },
  { key: 'symptoms', group: 'symptom', label: 'Symptoms', section: 'Symptoms documented',       dot: V1_ACCENT, tileBg: V1_ACCENT_TINT, tileFg: V1_ACCENT },
];

const useConditionGroups = () => {
  const { data, isLoading } = useGetResourceQuery('conditions');
  const groups: GroupedSummary[] = useMemo(
    () => (data ? [...data.named, ...(data.other ? [{ ...data.other, name: data.other.name || 'Other / Uncategorized' }] : [])] : []),
    [data]
  );
  return { groups, totalRecords: data?.totalRecords ?? 0, rawRecords: data?.rawRecords ?? 0, isLoading };
};

// --- 1. Hero "Your record at a glance" -------------------------------------
export const ConditionsHero: FC<SectionProps> = () => {
  const { groups, isLoading } = useConditionGroups();
  const y = groups.length;
  const counts = groups.reduce((acc, g) => { const k = bucketGroup(g); acc[k] = (acc[k] ?? 0) + 1; return acc; }, {} as Record<string, number>);
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const tile = (value: number | string, label: string, bg: string, fg: string, disabled = false) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center', opacity: disabled ? 0.45 : 1 }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            {/* Conditions aren't a "many readings per group" type, so the obs-style "X entries grouped into Y" framing
                reads oddly here (deduped X ≈ card Y); just the condition count (Leo 2026-07-30, option C). */}
            Your record contains <strong>{y.toLocaleString('en-US')}</strong> {y === 1 ? 'condition' : 'conditions and records'}.
          </Typography>
        </Box>
        {/* Symptoms LIVE (2026-07-13): SNOMED semantic-tag classification (suffix + snomed_codes). */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {COND_BUCKETS.filter((b) => (counts[b.group] ?? 0) > 0).map((b) => (
            <React.Fragment key={b.key}>{tile(counts[b.group] ?? 0, b.label, b.tileBg, b.tileFg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer — static "you may see many records" copy (reworded from "why are there so many
// records?" to avoid asserting a threshold count). Hidden only when the tab has NO records.
export const ConditionsExplainer: FC<SectionProps> = () => {
  const { totalRecords } = useConditionGroups();
  if (totalRecords === 0) return null;
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>You may see many records</Typography>
          <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
            Healthcare providers often document the same condition during many visits. A high number of records usually means a
            condition was referenced throughout your care over time — not that you received hundreds of separate diagnoses.
            Sections start collapsed so you can open just what you want to see.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

// --- 3+4+5. Filters + expandable sections + cards ---------------------------
// One component because the filter/search state drives the sections (recorded:
// Current/Past/Symptoms filters LIVE via classification; Records (admin) DEFERRED;
// sort pills discarded — ordering is Most-recent (date DESC); no per-card count (DB-deduped).
type FilterKey = 'all' | 'current' | 'past' | 'symptoms';

const CARD_DATE = (d: string) => (d ? formatDate(d) : '—');

const CODE_SYS_LABEL: Record<string, string> = {
  icd10: 'ICD-10', icd9: 'ICD-9', snomed: 'SNOMED', loinc: 'LOINC', rxnorm: 'RxNorm', cpt: 'CPT', cvx: 'CVX', ndc: 'NDC',
};
// Compact, grouped-by-system code line for a condition card: "ICD-10 C90.00 · SNOMED 12345". Surfaces the
// standard codes rolled into this name-card (a name can span several codes — cross-terminology or generic names).
const formatConditionCodes = (codes?: string[] | null): string => {
  if (!codes?.length) return '';
  const bySys = new Map<string, string[]>();
  for (const c of codes) {
    const i = c.indexOf('|');
    if (i < 0) continue;
    const sys = c.slice(0, i), code = c.slice(i + 1);
    if (code) { if (!bySys.has(sys)) bySys.set(sys, []); bySys.get(sys)!.push(code); }
  }
  return [...bySys.entries()].map(([sys, cs]) => `${CODE_SYS_LABEL[sys] ?? sys.toUpperCase()} ${cs.join(', ')}`).join(' · ');
};

const ConditionCard: FC<{ g: GroupedSummary; onOpen: () => void }> = ({ g, onOpen }) => {
  const chip = statusChip(g);
  const codeLine = formatConditionCodes(g.codes);
  return (
    <Box onClick={onOpen} sx={{
      bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
      '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.25 }}>{g.name}</Typography>
          {codeLine && <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 500, mt: 0.4 }}>{codeLine}</Typography>}
        </Box>
        {chip ? (
          <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg, whiteSpace: 'nowrap' }}>
            {chip.label}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 2.5, mt: 1.75 }}>
        {/* Same calendar day for first & last → show one "Date recorded" (CARD_DATE is day-level). */}
        {g.firstDate && CARD_DATE(g.firstDate) === CARD_DATE(g.lastDate) ? (
          <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Date recorded</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
        ) : (
          <>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>First documented</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Most recent</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.lastDate)}</Typography></Box>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1.75, pt: 1.5, borderTop: '1px solid #f0eef6' }}>
        {/* No per-card count for conditions — a code/name-grain count isn't meaningful (Leo 2026-07-30); dedup is in the DB (get_patient_conditions). */}
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View history ›</Typography>
      </Box>
    </Box>
  );
};

export const ConditionsBody: FC<SectionProps> = ({ openDetail }) => {
  const { groups, isLoading } = useConditionGroups();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  const q = search.trim().toLowerCase();
  const matches = (g: GroupedSummary) => !q || (g.name || 'other').toLowerCase().includes(q);

  const buckets = useMemo(() => {
    // Conditions have no per-card count (DB-deduped) → order by Most recent (date DESC); the DB already returns
    // them last_date-DESC, this keeps that after the client filter/split.
    const sorted = [...groups].sort((a, b) => (b.lastDate || '').localeCompare(a.lastDate || ''));
    return {
      current: sorted.filter((g) => bucketGroup(g) === 'current'),
      past: sorted.filter((g) => bucketGroup(g) === 'past'),
      symptoms: sorted.filter((g) => bucketGroup(g) === 'symptom'),
    };
  }, [groups]);

  if (isLoading) return null; // hero shows the spinner

  const pill = (label: string, active: boolean, onClick?: () => void, disabled = false) => (
    <Box key={label} onClick={disabled ? undefined : onClick} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: disabled ? 'default' : 'pointer',
      bgcolor: active ? V1_ACCENT : 'white', color: disabled ? '#b9b9be' : active ? 'white' : '#3a3a3c',
      border: `1px solid ${active ? V1_ACCENT : '#e0e0e6'}`, opacity: disabled ? 0.6 : 1,
    }}>{label}</Box>
  );

  // Default-open = the FIRST NON-EMPTY section in display order (Leo 2026-07-14: the top group's
  // cards are visible on entry — a hard-coded key fails when that bucket is empty for a patient).
  const firstNonEmpty = COND_BUCKETS.map((b) => b.key).find((k) => buckets[k].length > 0);
  const defaultOpen = (key: string) => key === firstNonEmpty;

  const section = (key: FilterKey & string, title: string, dot: string, list: GroupedSummary[]) => {
    const visible = (filter === 'all' || filter === key) && list.filter(matches).length > 0;
    if (!visible) return null;
    const full = list.filter(matches);
    const forced = !!q || filter === key;
    const isOpen = forced ? true : (open[key] ?? defaultOpen(key));
    const all = !!q || !!showAll[key];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, [key]: !(s[key] ?? defaultOpen(key)) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{title}</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {cards.map((g, i) => <ConditionCard key={`${g.name}-${i}`} g={g} onOpen={() => openDetail(g)} />)}
            </Box>
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, [key]: !s[key] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  // Disabled preview header (Symptoms / Administrative) — classifier deferred; same
  // "coming soon" language as the disabled filter pills (Leo).
  const previewSection = (title: string) => (
    <Paper key={title} elevation={0} sx={{ borderRadius: '18px', border: '1px dashed #e0e0e6', bgcolor: 'white', opacity: 0.55 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#d0d0d6', flex: 'none' }} />
        <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1, color: '#8e8e93' }}>{title}</Typography>
        <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#8e8e93', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>Coming soon</Box>
      </Box>
    </Paper>
  );

  const noResults = !!q && !COND_BUCKETS.some((b) => buckets[b.key].some(matches));

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search conditions" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('All', filter === 'all', () => setFilter('all'))}
        {/* Hide a classifier pill when its bucket is empty (nothing to show). */}
        {COND_BUCKETS.filter((b) => buckets[b.key].length > 0).map((b) => pill(b.label, filter === b.key, () => setFilter(b.key)))}
        {pill('Records', false, undefined, true)}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {COND_BUCKETS.map((b) => section(b.key, b.section, b.dot, buckets[b.key]))}
        {filter === 'all' && !q && previewSection('Administrative & monitoring records')}
      </Box>
      {noResults && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No records match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page organizes the condition records reported by your healthcare providers. It presents your health record as
          documented and does not interpret your conditions or provide medical advice.
        </Typography>
      </Box>
    </Container>
  );
};
